export type CreativeGroup = 'Banner' | 'Video' | 'Native' | null

const normalizeFormat = (format?: unknown | null): string | null => {
  if (format === undefined || format === null) return null
  return String(format)
}

export const mapFormatToGroup = (format?: unknown | null): CreativeGroup => {
  const normalizedFormat = normalizeFormat(format)
  if (!normalizedFormat) return null

  switch (normalizedFormat) {
    case 'ImageLink':
    case 'HTML':
    case 'HTML5':
      return 'Banner'

    case 'Video':
    case 'VideoVast':
      return 'Video'

    case 'NativeAds':
    case 'SimpleNativeAds':
      return 'Native'

    default:
      return null
  }
}

export const getCpcCreativeValidationError = (
  format?: unknown | null,
  existingGroup?: CreativeGroup,
  currentCreativeGroup?: CreativeGroup,
): string | null => {
  const normalizedFormat = normalizeFormat(format)
  const group = mapFormatToGroup(normalizedFormat)

  if (!group) {
    return `For CPC pricing type only Banner/Video/Native creatives are allowed. This creative format (${normalizedFormat}) is not allowed.`
  }

  const requiredGroup = currentCreativeGroup || existingGroup
  if (requiredGroup && group !== requiredGroup) {
    return `For CPC pricing type only one creative type per Ad Group is allowed. Current type: ${requiredGroup}, tried: ${group}.`
  }

  return null
}

export interface CpcCreativeCandidate {
  id?: number
  format?: unknown | null
}

export interface CpcCreativeValidationResult {
  invalidCreativeIds: number[]
  firstError: string | null
}

export const validateCpcCreativeSelection = (
  creatives: CpcCreativeCandidate[],
  creativeGroupsSnapshot?: Record<number, CreativeGroup>,
): CpcCreativeValidationResult => {
  let allowedGroup: CreativeGroup = null
  let firstError: string | null = null
  const invalidCreativeIds: number[] = []

  for (const creative of creatives) {
    const creativeId = creative.id
    const currentCreativeGroup = creativeId !== undefined ? creativeGroupsSnapshot?.[creativeId] ?? null : null
    const validationError = getCpcCreativeValidationError(creative.format, allowedGroup, currentCreativeGroup)

    if (validationError) {
      if (creativeId !== undefined) invalidCreativeIds.push(creativeId)
      if (!firstError) firstError = validationError
      continue
    }

    if (!allowedGroup) {
      allowedGroup = mapFormatToGroup(creative.format)
    }
  }

  return { invalidCreativeIds, firstError }
}
